import React, { useMemo } from 'react';
import type { SiteEvent, SeverityLevel } from '../../redux/api/eventsApi';
import { SEVERITY_HEX_COLORS } from '../../constants/eventStyles';

interface EventTimelineProps {
  events: SiteEvent[];
  height?: number;
  className?: string;
  onEventClick?: (eventId: string) => void;
}

const EventTimeline: React.FC<EventTimelineProps> = ({ events, height = 80, className = '', onEventClick }) => {
  const { timeScale, severityCounts } = useMemo(() => {
    if (!events.length) return { timeScale: null, severityCounts: { critical: 0, high: 0, medium: 0, low: 0 } };

    const dates = events.map(e => new Date(e.occurred_at).getTime());
    const min = Math.min(...dates);
    const max = Math.max(...dates);
    const range = max - min || 1;

    const counts = { critical: 0, high: 0, medium: 0, low: 0 };
    events.forEach(e => { counts[e.severity] = (counts[e.severity] || 0) + 1; });

    return {
      timeScale: { min, max, range },
      severityCounts: counts,
    };
  }, [events]);

  if (!events.length || !timeScale) {
    return (
      <div className={`flex items-center justify-center text-[var(--color-text-muted)] text-sm ${className}`} style={{ height }}>
        No events to display
      </div>
    );
  }

  const padding = 24;
  const dotRadius = 4;

  return (
    <div className={className}>
      <svg width="100%" height={height} className="overflow-visible">
        <line
          x1={`${padding}px`}
          y1={height / 2}
          x2={`calc(100% - ${padding}px)`}
          y2={height / 2}
          stroke="var(--color-border)"
          strokeWidth={1}
        />

        {events.map((event) => {
          const t = new Date(event.occurred_at).getTime();
          const pct = ((t - timeScale.min) / timeScale.range) * 100;
          const jitter = ((event.id.charCodeAt(0) % 5) - 2) * 4;

          return (
            <circle
              key={event.id}
              cx={`calc(${padding}px + ${pct}% * (1 - ${(2 * padding) / 100}))`}
              cy={height / 2 + jitter}
              r={dotRadius}
              fill={SEVERITY_HEX_COLORS[event.severity]}
              opacity={0.85}
              className="cursor-pointer hover:opacity-100 transition-opacity"
              onClick={() => onEventClick?.(event.id)}
            >
              <title>{`${event.title} (${event.severity})`}</title>
            </circle>
          );
        })}
      </svg>

      <div className="flex gap-3 px-2 text-xs text-[var(--color-text-secondary)]">
        {(Object.entries(severityCounts) as [SeverityLevel, number][])
          .filter(([, count]) => count > 0)
          .map(([severity, count]) => (
            <span key={severity} className="flex items-center gap-1">
              <span
                className="inline-block w-2 h-2 rounded-full"
                style={{ backgroundColor: SEVERITY_HEX_COLORS[severity] }}
              />
              {count} {severity}
            </span>
          ))}
      </div>
    </div>
  );
};

export default EventTimeline;
